import React, { useState, useEffect } from 'react';
import { Flame } from 'lucide-react';
import pop1 from '../assets/images/updates_popup/pop1.png';
import pop2 from '../assets/images/updates_popup/pop2.png';
import pop3 from '../assets/images/updates_popup/pop3.png';
import pop4 from '../assets/images/updates_popup/pop4.png';
import vid01 from '../assets/images/updates_popup/vid01.mp4';
import vid02 from '../assets/images/updates_popup/vid02.mp4';
import vid03 from '../assets/images/updates_popup/vid03.mp4';
import vid04 from '../assets/images/updates_popup/vid04.mp4';
import vid01_m from '../assets/images/updates_popup/vid01_m.mp4';
import vid02_m from '../assets/images/updates_popup/vid02_m.mp4';
import vid03_m from '../assets/images/updates_popup/vid03_m.mp4';
import vid04_m from '../assets/images/updates_popup/vid04_m.mp4';

const LatestUpdatesPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState('nano');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Cookie utility functions for cross-domain sharing
  const setCrossDomainCookie = (name, value, days) => {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    // Set cookie with domain=.ai-pro.org to share across all subdomains
    document.cookie = `${name}=${value};${expires};path=/;domain=.ai-pro.org;SameSite=Lax`;
  };

  const getCrossDomainCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  useEffect(() => {
    const checkAndShowPopup = () => {
      // Try to get from cookie first, then fallback to localStorage for migration
      let lastShown = getCrossDomainCookie('latestUpdatesPopupShown');
      
      // Migration: If not in cookie but exists in localStorage, migrate it
      if (!lastShown) {
        lastShown = localStorage.getItem('latestUpdatesPopupShown');
        if (lastShown) {
          // Migrate from localStorage to cookie
          setCrossDomainCookie('latestUpdatesPopupShown', lastShown, 365);
          console.log('Migrated popup timestamp from localStorage to cookie');
        }
      }
      
      const now = new Date().getTime();
      
      if (!lastShown) {
        setTimeout(() => {
          setIsVisible(true);
          console.log('Popup shown for first time');
        }, 1000);
      } else {
        const daysSinceShown = (now - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
        if (daysSinceShown >= 7) {
          setTimeout(() => {
            setIsVisible(true);
            console.log('Popup shown again after 7 days');
          }, 1000);
        } else {
          console.log(`Popup not shown - only ${daysSinceShown.toFixed(1)} days since last shown (need 7 days)`);
        }
      }
    };

    if (document.readyState === 'complete') {
      checkAndShowPopup();
    } else {
      window.addEventListener('load', checkAndShowPopup);
      return () => window.removeEventListener('load', checkAndShowPopup);
    }
  }, []);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent background scrolling when popup is visible
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  // Auto-rotate through updates every 10 seconds
  useEffect(() => {
    if (!isVisible) return;

    const updateIds = ['nano', 'veo', 'perplexity', 'prompt'];
    
    const interval = setInterval(() => {
      setSelectedUpdate((currentId) => {
        const currentIndex = updateIds.indexOf(currentId);
        const nextIndex = (currentIndex + 1) % updateIds.length;
        return updateIds[nextIndex];
      });
    }, 12000); // 12 seconds

    // Cleanup interval on unmount or when popup closes
    return () => clearInterval(interval);
  }, [isVisible]);

  const handleClose = () => {
    const now = new Date().getTime();
    setCrossDomainCookie('latestUpdatesPopupShown', now.toString(), 365);
    setIsVisible(false);
  };

  const handleCloserClick = () => {
    if (window.mixpanel) {
      window.mixpanel.track('new-feature-popup-close');
    }
    handleClose();
  };

  const handleUpdateClick = (updateType) => {
    setSelectedUpdate(updateType);
  };

  const handleTryNow = () => {
    if (window.mixpanel) {
      window.mixpanel.track('new-feature-popup-links', {
        feature: selectedUpdate
      });
    }

    // Check current location for staging or dev environment
    let currentLocation = window.location.href;
    let prefix = "";
    if (currentLocation.indexOf("staging") > -1) {
      prefix = "staging.";
    } else if (currentLocation.indexOf("dev") > -1) {
      prefix = "dev.";
    }
    
    // Redirect to appropriate URL based on selected update
    const urls = {
      veo: `https://${prefix}chatpro.ai-pro.org/video-gen/veo-3`,
      perplexity: `https://${prefix}chatpro.ai-pro.org/chat/perplexity`,
      nano: `https://${prefix}chatpro.ai-pro.org/chat/nano-banana`,
      prompt: `https://${prefix}start.ai-pro.org/document/prompt`
    };
    
    const targetUrl = urls[selectedUpdate];
    if (targetUrl) {
      window.location.href = targetUrl;
    }
    
    handleClose();
  };

  if (!isVisible) return null;

  const updates = [
    {
      id: 'nano',
      name: 'Gemini Nano Banana',
      icon: (
        <img 
          src={pop1} 
          alt="Gemini Nano Banana" 
          className="w-6 h-6 flex-shrink-0 object-contain"
        />
      ),
      title: 'Gemini Nano Banana',
      description: "Explore our Gemini Nano Banana — Google's latest AI model for fast and precise image generation.",
      videoSrc: isMobile ? vid01_m : vid01
    },
    {
      id: 'veo',
      name: 'Veo 3',
      icon: (
        <img 
          src={pop2} 
          alt="Veo 3" 
          className="w-6 h-6 flex-shrink-0 object-contain"
        />
      ),
      title: 'Veo 3',
      description: 'Experience Veo 3 — our most advanced video generation model with unprecedented quality and control.',
      videoSrc: isMobile ? vid02_m : vid02
    },
    {
      id: 'perplexity',
      name: 'Perplexity',
      icon: (
        <img 
          src={pop3} 
          alt="Perplexity" 
          className="w-6 h-6 flex-shrink-0 object-contain"
        />
      ),
      title: 'Perplexity',
      description: 'Get accurate, real-time answers with Perplexity — powered by advanced search and AI reasoning.',
      videoSrc: isMobile ? vid03_m : vid03
    },
    {
      id: 'prompt',
      name: 'Prompt Guide',
      icon: (
        <img 
          src={pop4} 
          alt="Prompt Guide" 
          className="w-6 h-6 flex-shrink-0 object-contain"
        />
      ),
      title: 'Prompt Guide',
      description: 'Master AI prompts with our comprehensive guide — unlock better results with expert techniques and examples.',
      videoSrc: isMobile ? vid04_m : vid04
    }
  ];

  const currentUpdate = updates.find(u => u.id === selectedUpdate) || updates[0];

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-5 animate-[fadeIn_0.3s_ease-in-out] font-['Inter']"
    >
      <div className="relative max-w-[990px] w-full">
        {/* Close Button - Outside container on desktop */}
        <button 
          className="closer absolute top-4 right-4 md:top-4 md:right-0 bg-transparent border-none cursor-pointer p-2 flex items-center justify-center rounded-lg transition-colors duration-200 z-10 text-gray-700 lg:text-white hover:bg-black hover:bg-opacity-5 lg:mr-[-45px] lg:mt-[-26px]"
          onClick={handleCloserClick}
          aria-label="Close popup"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div 
          className="relative bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] w-full max-h-[90vh] overflow-hidden animate-[slideUp_0.3s_ease-out]"
        >
          <div className="flex flex-col md:flex-row md:min-h-[550px]">
          {/* Video Section - Top on mobile, Right on desktop */}
          <div className="w-full md:w-[55%] relative overflow-hidden flex items-end order-1 md:order-2 aspect-video md:aspect-auto md:min-h-0">
            {/* Background Local Video - Wrapper to crop UI elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden flex items-center justify-center">
              <video
                key={currentUpdate.id}
                src={currentUpdate.videoSrc}
                autoPlay
                muted
                loop
                playsInline
                className={`pointer-events-none object-cover !w-full !h-[180%] md:!w-[177.78vh] md:!h-[101%] md:!top-[50%] ${
                  selectedUpdate === 'nano' ? '!top-[65%]' : '!top-[50%]'
                }`}
                style={{ 
                  position: 'absolute',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
              />
            </div>
            
            {/* Black gradient overlay at bottom - Desktop only */}
            <div className="hidden md:block absolute inset-0 bg-gradient-to-t from-black/90 via-black/0 to-transparent"></div>
            
            {/* Content at bottom - Desktop only */}
            <div className="hidden md:block w-full relative z-10 p-6">
              <h3 className="text-[16px] font-bold text-white leading-tight">
                {currentUpdate.title}
              </h3>
              <p className="text-[16px] text-white leading-tight m-0 mb-4">
                {currentUpdate.description}
              </p>
              <button 
                className="bg-gradient-to-r from-[#3E53B8] from-30% to-[#2393F1] to-70% text-white border-none px-8 py-3.5 rounded-full text-base font-semibold cursor-pointer transition-all duration-200 shadow-[0_4px_12px_rgba(37,99,235,0.3)] hover:opacity-90 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(37,99,235,0.4)] active:translate-y-0 w-full md:w-auto"
                onClick={handleTryNow}
              >
                Try {currentUpdate.name}
              </button>
            </div>
          </div>

          {/* Content Section - Bottom on mobile, Left on desktop */}
          <div className="w-full md:w-[45%] p-6 md:p-8 md:py-12 bg-white flex flex-col order-2 md:order-1 overflow-y-auto max-h-[60vh] md:max-h-none">
            <div className="mb-6 md:mb-8">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-4">
                <Flame className="w-3.5 h-3.5" />
                What's New
              </div>
              <h2 className="text-[24px] md:text-[32px] font-bold text-gray-900 m-0 mb-3 leading-tight">
                Latest Updates
              </h2>
              <p className="text-sm md:text-base text-black m-0 leading-relaxed mb-4 md:mb-0">
                Explore Chatbot Pro's new powerful features — boost creativity and results.
              </p>
            </div>

            <div className="flex flex-col gap-0 mb-6 md:mb-0">
              {updates.map((update, index) => (
                <div
                  key={update.id}
                  className={`relative p-4 cursor-pointer transition-all duration-200 hover:bg-gray-50 border-l-4 ${
                    index < updates.length - 1 ? 'border-b-[1px] border-gray-100' : ''
                  } ${selectedUpdate === update.id ? 'border-l-blue-600' : 'border-l-gray-100'}`}
                  onClick={() => handleUpdateClick(update.id)}
                >
                  <div className="flex items-center gap-3">
                    {update.icon}
                    <span className={`text-base font-medium ${
                      selectedUpdate === update.id ? 'text-blue-600' : 'text-gray-900'
                    }`}>{update.name}</span>
                  </div>
                  
                  {/* Mobile: Show description when this item is selected */}
                  {selectedUpdate === update.id && (
                    <div className="md:hidden mt-1 ml-[38px]">
                      <p className="text-[12px] text-gray-600 leading-relaxed">
                        {update.description}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile: Button at bottom */}
            <button 
              className="md:hidden bg-gradient-to-r from-[#3E53B8] from-30% to-[#2393F1] to-70% text-white border-none px-8 py-3.5 rounded-full text-base font-semibold cursor-pointer transition-all duration-200 shadow-[0_4px_12px_rgba(37,99,235,0.3)] hover:opacity-90 active:translate-y-0 w-full"
              onClick={handleTryNow}
            >
              Try {currentUpdate.name}
            </button>
          </div>
        </div>
      </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default LatestUpdatesPopup;

